import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import ImageWithFallback from "./ImageWithFallback";

function pickImage(tier) {
    return (
        tier?.image ||
        tier?.img ||
        tier?.photo ||
        tier?.images?.[0] ||
        tier?.bilder?.[0] ||
        ""
    );
}

function formatDate(dateStr) {
    if (!dateStr) return "";
    const [y, m, d] = String(dateStr).split("-");
    if (!y || !m || !d) return dateStr;
    return `${d}.${m}.${y}`;
}

export default function ParentDogCard({
                                          tier,
                                          index = 0,
                                          primaryColor,
                                          secondaryColor,
                                          styles,
                                      }) {
    const [open, setOpen] = useState(false);
    const imgSrc = useMemo(() => pickImage(tier), [tier]);

    const displayName = tier?.callName ? `${tier.callName}` : tier?.name || "Hund";
    const subtitle =
        tier?.callName && tier?.name
            ? tier.name
            : [tier?.breed, tier?.age].filter(Boolean).join(" • ");

    const healthText =
        typeof tier?.health === "string"
            ? tier.health
            : [
                tier?.health?.patella && `Patella: ${tier.health.patella}`,
                tier?.health?.eyes && `Augen: ${tier.health.eyes}`,
            ]
                .filter(Boolean)
                .join(" • ");

    const titlesList = Array.isArray(tier?.titles) ? tier.titles : [];
    const awardsList = Array.isArray(tier?.awards) ? tier.awards : [];
    const showResults = Array.isArray(tier?.showResults) ? tier.showResults : [];

    const ztp = tier?.breeding?.approvedSince;

    // Optional steuerbar pro Hund:
    // tier.imageFit = "cover" | "contain"
    // tier.imagePosition = "50% 40%" (oder any CSS object-position)
    const imageFit = tier?.imageFit || "cover";
    const imagePosition = tier?.imagePosition || "center";

    const cardRadius = styles?.elternCard?.borderRadius ?? 18;
    const imageRadius = Math.max(12, Math.round(cardRadius - 4)); // optisch passend

    return (
        <motion.div
            style={{
                ...styles.elternCard,
                overflow: "hidden",
                borderRadius: cardRadius,
            }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: Math.min(index * 0.08, 0.2) }}
            viewport={{ once: true }}
            whileHover={{ y: -8, boxShadow: "0 15px 30px rgba(0,0,0,0.10)" }}
        >
            {/* IMAGE FRAME: immer mittig + abgerundet */}
            <div
                style={{
                    ...styles.elternImageContainer,
                    position: "relative",
                    aspectRatio: "4 / 3",
                    background: "#F4F4F4",
                    overflow: "hidden",
                    padding: 12,

                    // wichtig: damit das Bild wirklich IMMER mittig sitzt
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                {/* Innerer Rahmen fürs saubere Abrunden + Clip */}
                <div
                    style={{
                        width: "100%",
                        height: "100%",
                        borderRadius: imageRadius,
                        overflow: "hidden",
                        background: "#fff",
                        boxShadow: "0 10px 24px rgba(0,0,0,0.08)",
                    }}
                >
                    <ImageWithFallback
                        src={imgSrc}
                        alt={tier?.callName ? `${tier.callName} – ${tier.name}` : tier?.name}
                        style={{
                            width: "100%",
                            height: "100%",
                            display: "block",
                            objectFit: imageFit,
                            objectPosition: imagePosition,
                        }}
                    />
                </div>
            </div>

            <div style={styles.elternInfo}>
                <h3
                    style={{
                        color: primaryColor,
                        fontFamily: "'Playfair Display', serif",
                        marginBottom: 6,
                    }}
                >
                    {displayName}
                </h3>

                {subtitle && (
                    <p style={{ ...styles.elternBreed, marginTop: 0 }}>{subtitle}</p>
                )}

                {(titlesList[0] || ztp || healthText) && (
                    <p
                        style={{
                            margin: "10px 0 12px",
                            color: secondaryColor || "#666",
                            fontSize: 13,
                            lineHeight: 1.5,
                        }}
                    >
                        {titlesList[0] ? `${titlesList[0]}` : ""}
                        {titlesList[0] && (ztp || healthText) ? " · " : ""}
                        {ztp ? `ZTP: ${formatDate(ztp)}` : ""}
                        {ztp && healthText ? " · " : ""}
                        {healthText ? healthText : ""}
                    </p>
                )}

                {tier?.description && (
                    <p style={styles.elternDescription}>{tier.description}</p>
                )}

                <button
                    type="button"
                    onClick={() => setOpen((v) => !v)}
                    style={{
                        width: "100%",
                        border: "none",
                        borderRadius: 14,
                        padding: "10px 12px",
                        cursor: "pointer",
                        background: "rgba(0,0,0,0.04)",
                        fontWeight: 800,
                        color: "#222",
                        marginTop: 8,
                    }}
                >
                    {open ? "Weniger Details" : "Mehr Details"}
                </button>

                <AnimatePresence>
                    {open && (
                        <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.22 }}
                            style={{ overflow: "hidden" }}
                        >
                            <div style={{ ...styles.elternDetails, marginTop: 10 }}>
                                {tier?.character && (
                                    <div style={styles.elternDetail}>
                                        <h4>Charakter</h4>
                                        <p>{tier.character}</p>
                                    </div>
                                )}

                                {awardsList.length > 0 && (
                                    <div style={styles.elternDetail}>
                                        <h4>Auszeichnungen</h4>
                                        <ul style={styles.elternList}>
                                            {awardsList.map((award, i) => (
                                                <li key={i}>{award}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {showResults.length > 0 && (
                                    <div style={styles.elternDetail}>
                                        <h4>Ausstellungen</h4>
                                        <ul style={styles.elternList}>
                                            {showResults.slice(0, 4).map((r, i) => (
                                                <li key={i}>
                                                    {r.date} – {r.place}: {r.result}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {healthText && (
                                    <div style={styles.elternDetail}>
                                        <h4>Gesundheit</h4>
                                        <p>{healthText}</p>
                                    </div>
                                )}

                                {(tier?.parents?.sire?.name || tier?.parents?.dam?.name) && (
                                    <div style={styles.elternDetail}>
                                        <h4>Abstammung</h4>
                                        <p style={{ margin: 0 }}>
                                            <strong>Vater:</strong> {tier.parents?.sire?.name || "-"}
                                            <br />
                                            <strong>Mutter:</strong> {tier.parents?.dam?.name || "-"}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </motion.div>
    );
}
